import { useEffect, useRef } from "react";

/**
 * Re-runs `load` on an interval so the dashboard keeps up with devices without
 * anyone pressing refresh.
 *
 * Polling rather than a pushed stream: missions change at most once a minute
 * (that is the app's own sync interval), and a handful of people look at this
 * at once. A persistent connection per viewer would cost real infrastructure
 * to save a latency nobody would notice.
 *
 * Paused while the tab is hidden, and run immediately when it comes back — a
 * background tab polling all afternoon is wasted work on both ends, and the
 * moment someone looks again is exactly when the data should be current.
 */
export function usePolling(load: () => void | Promise<void>, intervalMs: number) {
  // Held in a ref so a changing closure does not restart the timer, which
  // would reset the interval on every render.
  const latest = useRef(load);
  latest.current = load;

  useEffect(() => {
    let timer: number | undefined;

    const tick = () => {
      if (document.visibilityState === "visible") void latest.current();
    };

    const start = () => {
      window.clearInterval(timer);
      timer = window.setInterval(tick, intervalMs);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        tick();
        start();
      } else {
        window.clearInterval(timer);
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
